import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, RefreshCw } from 'lucide-react';
import api from '../services/api';

const ChangeRequestsPage = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [reviewModal, setReviewModal] = useState(null);
  const [reviewComments, setReviewComments] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchRequests(); }, [filter]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/users/change-requests?status=${filter}`);
      setRequests(response.data.data.requests);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (request) => {
    setProcessing(true);
    try {
      await api.put(`/users/change-requests/${request.id}/approve`, { reviewComments });
      setReviewModal(null);
      setReviewComments('');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to approve request');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (request) => {
    setProcessing(true);
    try {
      await api.put(`/users/change-requests/${request.id}/reject`, { reviewComments });
      setReviewModal(null);
      setReviewComments('');
      fetchRequests();
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to reject request');
    } finally {
      setProcessing(false);
    }
  };

  const filterCfg = {
    pending:  { color: '#F59E0B', bg: '#FFFBEB', border: '#FCD34D', active: '#F59E0B' },
    approved: { color: '#10B981', bg: '#ECFDF5', border: '#6EE7B7', active: '#10B981' },
    rejected: { color: '#EF4444', bg: '#FEF2F2', border: '#FCA5A5', active: '#EF4444' },
  };

  const roleColors = {
    manager:    { bg: '#EFF6FF', color: '#1D4ED8' },
    supervisor: { bg: '#F5F3FF', color: '#6D28D9' },
  };

  return (
    <div style={{ maxWidth: '900px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#111827', margin: '0 0 4px' }}>Change Requests</h1>
          <p style={{ fontSize: '14px', color: '#6B7280', margin: 0 }}>Review and manage user profile change requests</p>
        </div>
        <button onClick={fetchRequests} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', boxShadow: '0 1px 2px rgba(0,0,0,0.05)' }}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Filter Tabs */}
      <div style={{ background: 'white', borderRadius: '12px', padding: '6px', display: 'flex', gap: '4px', marginBottom: '24px', border: '1px solid #F3F4F6', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
        {['pending', 'approved', 'rejected'].map(status => {
          const isActive = filter === status;
          const cfg = filterCfg[status];
          return (
            <button key={status} onClick={() => setFilter(status)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '10px', borderRadius: '8px', border: 'none', cursor: 'pointer', fontSize: '14px', fontWeight: '600', transition: 'all 0.2s',
                background: isActive ? cfg.active : 'transparent',
                color: isActive ? 'white' : '#6B7280',
              }}>
              {status === 'pending' ? <Clock size={16} /> : status === 'approved' ? <CheckCircle size={16} /> : <XCircle size={16} />}
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <div style={{ width: '40px', height: '40px', border: '4px solid #e59225', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
        </div>
      ) : requests.length === 0 ? (
        <div style={{ background: 'white', borderRadius: '12px', padding: '64px 24px', textAlign: 'center', border: '1px solid #F3F4F6' }}>
          <Clock size={48} color="#D1D5DB" style={{ margin: '0 auto 16px', display: 'block' }} />
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827', margin: '0 0 8px' }}>No {filter} requests</h3>
          <p style={{ color: '#6B7280', fontSize: '14px' }}>There are no {filter} change requests at the moment.</p>
        </div>
      ) : (
        requests.map(request => {
          const cfg = filterCfg[request.status] || filterCfg.pending;
          const roleCfg = roleColors[request.user?.role] || { bg: '#F3F4F6', color: '#374151' };
          return (
            <div key={request.id} style={{ background: 'white', borderRadius: '12px', border: '1px solid #F3F4F6', marginBottom: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
              {/* Card Header */}
              <div style={{ padding: '16px 20px', background: '#F9FAFB', borderBottom: '1px solid #F3F4F6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  {/* Avatar */}
                  <div style={{ width: '42px', height: '42px', background: '#FFF7ED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <span style={{ color: '#e59225', fontWeight: '700', fontSize: '16px' }}>{request.user?.name?.charAt(0)?.toUpperCase()}</span>
                  </div>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '600', color: '#111827', fontSize: '15px' }}>{request.user?.name}</span>
                      <span style={{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '12px', background: roleCfg.bg, color: roleCfg.color, textTransform: 'capitalize' }}>{request.user?.role}</span>
                    </div>
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: '2px 0 0' }}>{request.user?.email}</p>
                  </div>
                </div>
                <span style={{ fontSize: '12px', color: '#9CA3AF' }}>
                  {new Date(request.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              </div>

              {/* Card Body */}
              <div style={{ padding: '20px' }}>
                {/* Changes Comparison */}
                <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '12px' }}>Requested Changes</div>
                {Object.entries(request.requestedChanges || {}).map(([field, value]) => {
                  const currentValue = field === 'name' ? request.user?.name : field === 'email' ? request.user?.email : request.user?.phone || 'Not set';
                  return (
                    <div key={field} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 30px 1fr', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase' }}>{field}</span>
                      <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '8px 12px', border: '1px solid #F3F4F6' }}>
                        <div style={{ fontSize: '10px', color: '#9CA3AF', marginBottom: '2px' }}>Current</div>
                        <div style={{ fontSize: '13px', color: '#374151' }}>{currentValue}</div>
                      </div>
                      <div style={{ textAlign: 'center', color: '#e59225', fontWeight: '700', fontSize: '18px' }}>→</div>
                      <div style={{ background: '#FFF7ED', borderRadius: '8px', padding: '8px 12px', border: '1px solid #FED7AA' }}>
                        <div style={{ fontSize: '10px', color: '#e59225', marginBottom: '2px' }}>Requested</div>
                        <div style={{ fontSize: '13px', fontWeight: '600', color: '#92400E' }}>{value}</div>
                      </div>
                    </div>
                  );
                })}

                {/* Reason */}
                {request.requestReason && (
                  <div style={{ background: '#F9FAFB', borderRadius: '8px', padding: '10px 14px', border: '1px solid #F3F4F6', marginTop: '12px' }}>
                    <div style={{ fontSize: '11px', fontWeight: '700', color: '#9CA3AF', textTransform: 'uppercase', marginBottom: '4px' }}>Reason</div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>{request.requestReason}</div>
                  </div>
                )}

                {/* Review Info */}
                {request.status !== 'pending' && request.reviewer && (
                  <div style={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: '8px', padding: '12px 14px', marginTop: '12px' }}>
                    <p style={{ fontSize: '12px', color: '#6B7280', margin: 0 }}>
                      Reviewed by <strong>{request.reviewer?.name}</strong> on {new Date(request.reviewedAt).toLocaleString()}
                    </p>
                    {request.reviewComments && (
                      <p style={{ fontSize: '13px', color: '#374151', margin: '6px 0 0', fontStyle: 'italic' }}>"{request.reviewComments}"</p>
                    )}
                  </div>
                )}

                {/* Actions */}
                {request.status === 'pending' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #F3F4F6' }}>
                    <button onClick={() => setReviewModal({ request, action: 'reject' })}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 18px', background: '#FEF2F2', color: '#EF4444', border: '1px solid #FECACA', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}>
                      <XCircle size={15} /> Reject
                    </button>
                    <button onClick={() => setReviewModal({ request, action: 'approve' })}
                      style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 18px', background: '#10B981', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                      <CheckCircle size={15} /> Approve
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })
      )}

      {/* Review Modal */}
      {reviewModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: 'white', borderRadius: '16px', maxWidth: '440px', width: '100%', overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            {/* Modal Header */}
            <div style={{ padding: '20px 24px', background: reviewModal.action === 'approve' ? '#10B981' : '#EF4444', display: 'flex', alignItems: 'center', gap: '10px' }}>
              {reviewModal.action === 'approve' ? <CheckCircle size={22} color="white" /> : <XCircle size={22} color="white" />}
              <h3 style={{ color: 'white', margin: 0, fontSize: '17px', fontWeight: '700' }}>
                {reviewModal.action === 'approve' ? 'Approve' : 'Reject'} Change Request
              </h3>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              {/* User Info */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: '#F9FAFB', borderRadius: '10px', padding: '14px', marginBottom: '16px' }}>
                <div style={{ width: '40px', height: '40px', background: '#FFF7ED', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ color: '#e59225', fontWeight: '700' }}>{reviewModal.request.user?.name?.charAt(0)?.toUpperCase()}</span>
                </div>
                <div>
                  <div style={{ fontWeight: '600', color: '#111827', fontSize: '14px' }}>{reviewModal.request.user?.name}</div>
                  <div style={{ fontSize: '12px', color: '#6B7280' }}>{reviewModal.request.user?.email}</div>
                </div>
              </div>

              <p style={{ fontSize: '14px', color: '#374151', marginBottom: '16px' }}>
                Are you sure you want to <strong>{reviewModal.action}</strong> this request?
                {reviewModal.action === 'approve' && <span style={{ display: 'block', color: '#10B981', fontSize: '12px', marginTop: '4px' }}>✅ The user's profile will be updated immediately.</span>}
              </p>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '6px' }}>
                  Comments <span style={{ color: '#9CA3AF', fontWeight: '400' }}>(Optional)</span>
                </label>
                <textarea value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} rows="3"
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #E5E7EB', borderRadius: '8px', fontSize: '14px', resize: 'none', outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }}
                  placeholder="Add a comment for the user..." />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={() => { setReviewModal(null); setReviewComments(''); }} disabled={processing}
                  style={{ padding: '9px 18px', background: 'white', border: '1px solid #E5E7EB', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', color: '#374151', fontWeight: '500' }}>
                  Cancel
                </button>
                <button
                  onClick={() => reviewModal.action === 'approve' ? handleApprove(reviewModal.request) : handleReject(reviewModal.request)}
                  disabled={processing}
                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '9px 20px', background: reviewModal.action === 'approve' ? '#10B981' : '#EF4444', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600', opacity: processing ? 0.6 : 1 }}>
                  {processing ? (
                    <><div style={{ width: '14px', height: '14px', border: '2px solid white', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 1s linear infinite' }} /> Processing...</>
                  ) : (
                    <>{reviewModal.action === 'approve' ? <CheckCircle size={15} /> : <XCircle size={15} />} Confirm {reviewModal.action === 'approve' ? 'Approval' : 'Rejection'}</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChangeRequestsPage;